// ./src/components/features/Budget/BudgetItemEditDrawer/BudgetItemEditDrawer.tsx
//
// Create/edit drawer for an event budget item (expected expense). Self-loading in
// edit mode via recordId; context-or-props resolution; no success toast (the page
// highlight is the confirmation). The amount fields switch on the calc type:
// fixed → one amount; price × quantity → unit price + quantity; per-head → price
// per guest; per RSVP group → price per guest + a group multi-select. In edit mode
// the derived expected amount is shown read-only. `eventId` is set on CREATE only.
// Mirrors TaskEditDrawer.
// #A4.7 — the event's "Staff pay" line (`isSystem`) is kept by the app: its name, category, due date, responsible and
// notes are editable, but its calc type and amount are not (the server refuses them), so they are neither shown nor
// sent — the drawer shows the line's breakdown by role and points to Volunteers → Staff pay instead.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Alert, Anchor, Stack, TextInput, NumberInput, Textarea, Skeleton, Group, Text, Box } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { DatePickerInput } from "@mantine/dates";

import { useResponsiveDatePickerProps } from "@/components/generic/form";
import { OpResult } from "@sdflc/api-helpers";

import { BudgetItem, BudgetItemInput, BUDGET_CALC_TYPE, StaffPay, StaffPayService } from "@/utils/api";
import { getFormErrors, formatErrors, formatCurrency } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS, useRsvpGroups } from "@/hooks";
import { useOptionalBudgetItemsContext } from "@/contexts/BudgetItemsContext";
import { AppDrawer, AppIcon, CategorySelect, SelectField, MultiSelectField } from "@/components/generic";
import { ResponsiblePartySelect } from "@/components/features/Tasks/ResponsiblePartySelect/ResponsiblePartySelect";

import { MoneyInput } from "../MoneyInput/MoneyInput";
import { CALC_TYPE_META, getCalcTypeMeta, isPerHeadCalcType } from "../budgetHelpers";

const KNOWN_FORM_FIELDS = [
  "name",
  "categoryId",
  "calcType",
  "unitAmount",
  "quantity",
  "rsvpGroupIds",
  "glCode",
  "dueDate",
  "responsibleKind",
  "responsibleId",
  "description",
];

export interface BudgetItemEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this item belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** The event's currency, stamped on new items. */
  eventCurrency?: string | null;
  /** A name for a NEW item (the create wizard's one-tap starters, "Venue"). Ignored when editing. */
  defaultName?: string | null;
  onCreated?: (item: BudgetItem) => void;
  onUpdated?: (item: BudgetItem) => void;
}

interface FormData {
  name: string;
  categoryId: string | null;
  calcType: string;
  unitAmount: number | null;
  quantity: number | null;
  rsvpGroupIds: string[];
  glCode: string;
  dueDate: string | null;
  responsibleKind: string | null;
  responsibleId: string | null;
  description: string;
}

const getInitialFormData = (defaultName?: string | null): FormData => ({
  name: defaultName ?? "",
  categoryId: null,
  calcType: BUDGET_CALC_TYPE.FIXED,
  unitAmount: null,
  quantity: null,
  rsvpGroupIds: [],
  glCode: "",
  dueDate: null,
  responsibleKind: null,
  responsibleId: null,
  description: "",
});

const fromRecord = (record: BudgetItem): FormData => ({
  name: record.name || "",
  categoryId: record.categoryId || null,
  calcType: record.calcType || BUDGET_CALC_TYPE.FIXED,
  unitAmount: record.unitAmount ?? null,
  quantity: record.quantity ?? null,
  rsvpGroupIds: Array.isArray(record.rsvpGroupIds) ? record.rsvpGroupIds : [],
  glCode: record.glCode || "",
  dueDate: record.dueDate || null,
  responsibleKind: record.responsibleKind || null,
  responsibleId: record.responsibleId || null,
  description: record.description || "",
});

const serialize = (data: FormData): string => JSON.stringify(data);

const BudgetItemEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={40} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
  </Stack>
);

export const BudgetItemEditDrawer: React.FC<BudgetItemEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  eventCurrency,
  defaultName,
  onCreated,
  onUpdated,
}) => {
  const responsiveDatePicker = useResponsiveDatePickerProps();
  const navigate = useNavigate();
  const ctx = useOptionalBudgetItemsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<BudgetItem | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as BudgetItem);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  const { groups: rsvpGroups } = useRsvpGroups(opened ? eventId || record?.eventId || null : null);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  // Own submit result first — a context `lastError` alone swallows errors when the caller saves another way.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const effectiveError = loadError || submitError || operationError;

  // #A4.7 — the Staff pay line: its breakdown by role, read from staff pay (the budget page already needs budget view).
  const isSystem = !!record?.isSystem;
  const systemEventId = record?.eventId || eventId || null;
  const [staffPay, setStaffPay] = useState<StaffPay | null>(null);

  useEffect(() => {
    if (!opened || !isSystem || !systemEventId) {
      setStaffPay(null);

      return undefined;
    }

    let cancelled = false;

    StaffPayService.get(systemEventId).then((result) => {
      if (!cancelled && result.didSucceed()) {
        setStaffPay((result.getDataFirst() as StaffPay) ?? null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [opened, isSystem, systemEventId]);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData(defaultName);
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    setSubmitError(null);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, defaultName]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleResponsibleChange = useCallback((kind: string | null, id: string | null) => {
    setFormData((prev) => ({ ...prev, responsibleKind: kind, responsibleId: id }));
  }, []);

  const calcMeta = getCalcTypeMeta(formData.calcType);
  const currency = record?.currencyCode || eventCurrency || "USD";
  const isFixed = formData.calcType === BUDGET_CALC_TYPE.FIXED;
  const isPriceQuantity = formData.calcType === BUDGET_CALC_TYPE.PRICE_QUANTITY;
  const isByGroup = formData.calcType === BUDGET_CALC_TYPE.BY_RSVP_GROUP;
  const isPerHead = isPerHeadCalcType(formData.calcType);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);
    setSubmitError(null);

    const submitData: BudgetItemInput = {
      name: formData.name.trim() || null,
      categoryId: formData.categoryId,
      glCode: formData.glCode.trim() || null,
      dueDate: formData.dueDate || null,
      responsibleKind: formData.responsibleKind,
      responsibleId: formData.responsibleId,
      description: formData.description.trim() || null,
    };

    // ⚠ The Staff pay line's amount is the app's: sending any of these is refused, so a system line sends none.
    if (!isSystem) {
      submitData.calcType = formData.calcType;
      submitData.unitAmount = formData.unitAmount ?? 0;
      submitData.quantity = isPriceQuantity ? formData.quantity ?? null : null;
      submitData.rsvpGroupIds = isByGroup ? formData.rsvpGroupIds : [];
    }

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
      // (No currency: #A4.7a — a budget line is in the EVENT's currency, read by the server.)
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result && !result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as BudgetItem[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [
    formData,
    isEditMode,
    recordId,
    eventId,
    isPriceQuantity,
    isByGroup,
    isSystem,
    createFn,
    updateFn,
    onClose,
    onCreated,
    onUpdated,
  ]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit budget item" : "Add budget item"}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <BudgetItemEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="What's the expense?"
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            required
            data-autofocus
            size="md"
          />

          <CategorySelect
            group="budget"
            label="Category"
            description="Group similar expenses (catering, venue, décor…)."
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          {isSystem ? (
            <Alert color="blue" variant="light" icon={<AppIcon iconName="Cash" size={18} />} title="Kept up to date by the app">
              <Text size="md">
                This is the event&apos;s staff pay: what paid volunteers earn — expected for shifts ahead and open places, actual
                once they check out. Change rates, breaks and bonuses on the volunteer pages; record payments with Mark paid.
              </Text>
              {systemEventId ? (
                <Anchor
                  component="button"
                  type="button"
                  size="md"
                  mt="xs"
                  onClick={() => {
                    onClose();
                    navigate(`/events/${systemEventId}/volunteers/staff-pay`);
                  }}
                >
                  Open Staff pay
                </Anchor>
              ) : null}
            </Alert>
          ) : (
            <>
            <SelectField
              label="How is the amount calculated?"
              description={calcMeta?.hint}
              data={CALC_TYPE_META.map((m) => ({ value: m.value, label: m.label }))}
              value={formData.calcType}
              onChange={(_name, v) => setField("calcType", v || BUDGET_CALC_TYPE.FIXED)}
              leftSection={<AppIcon iconName={calcMeta?.icon || "Coin"} size={16} />}
              error={fieldErrors.calcType}
              allowDeselect={false}
              size="md"
            />

            {/* Amount fields switch on the calc type. */}
            <MoneyInput
              label={isFixed ? "Amount" : "Unit price"}
              description={isPerHead ? "Charged per guest." : undefined}
              value={formData.unitAmount}
              onChange={(v) => setField("unitAmount", v)}
              currency={currency}
              error={fieldErrors.unitAmount}
              required
            />

            {isPriceQuantity ? (
              <NumberInput
                label="Quantity"
                placeholder="1"
                value={formData.quantity ?? ""}
                onChange={(v) => setField("quantity", v === "" || v === null ? null : Number(v))}
                min={0}
                step={1}
                error={fieldErrors.quantity}
                size="md"
              />
            ) : null}

            {isByGroup ? (
              <MultiSelectField
                label="RSVP groups"
                description="The item's cost is unit price × guests in these group(s)."
                placeholder={rsvpGroups.length ? "Pick RSVP group(s)" : "No RSVP groups on this event yet"}
                data={rsvpGroups.filter((g) => g.id).map((g) => ({ value: g.id as string, label: g.name || "Untitled group" }))}
                value={formData.rsvpGroupIds}
                onChange={(_name, v) => setField("rsvpGroupIds", v)}
                error={fieldErrors.rsvpGroupIds}
                searchable
                clearable
                size="md"
              />
            ) : null}
            </>
          )}

          {isSystem && staffPay && staffPay.byRole.length > 0 ? (
            <Stack gap={6}>
              <Text size="md" fw={600}>
                By role
              </Text>
              {staffPay.byRole.map((role) => (
                <Group key={role.roleId ?? "none"} justify="space-between" wrap="nowrap">
                  <Text size="md" lineClamp={1}>
                    {role.roleName || "No role"}
                  </Text>
                  <Text size="md" fw={600} style={{ whiteSpace: "nowrap" }}>
                    {formatCurrency(role.totals.total, currency)}
                  </Text>
                </Group>
              ))}
            </Stack>
          ) : null}

          {isEditMode && record ? (
            <Box
              style={{
                padding: "var(--mantine-spacing-sm)",
                borderRadius: "var(--mantine-radius-md)",
                border: "1px solid var(--mantine-color-default-border)",
              }}
            >
              <Group gap="xs" wrap="nowrap">
                <AppIcon iconName="Calculator" size={16} />
                <Text size="sm" c="dimmed">
                  Expected now:
                </Text>
                <Text size="md" fw={600}>
                  {formatCurrency(record.expectedAmount ?? 0, currency)}
                </Text>
              </Group>
            </Box>
          ) : null}

          <DatePickerInput
            label="Due date"
            placeholder="When is it due?"
            value={formData.dueDate || null}
            onChange={(v) => setField("dueDate", (v as unknown as string) || null)}
            valueFormat="MMM DD, YYYY"
            error={fieldErrors.dueDate}
            clearable
            size="md"
            {...responsiveDatePicker}
          />

          <TextInput
            label="GL code"
            placeholder="Accounting / ledger code (optional)"
            value={formData.glCode}
            onChange={(e) => setField("glCode", e.currentTarget.value)}
            error={fieldErrors.glCode}
            size="md"
          />

          <ResponsiblePartySelect
            eventId={eventId || record?.eventId || ""}
            value={{ kind: formData.responsibleKind, id: formData.responsibleId }}
            onChange={handleResponsibleChange}
            fallbackLabel={record?.responsibleLabel}
            label="Who is responsible"
            kindError={fieldErrors.responsibleKind}
            idError={fieldErrors.responsibleId}
          />

          <Textarea
            label="Description"
            placeholder="Notes, scope, details…"
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />
        </Stack>
      )}
    </AppDrawer>
  );
};

export default BudgetItemEditDrawer;
